import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Alert,
  TextInput,
  ActionSheetIOS,
  useWindowDimensions,
  type ViewStyle,
  type StyleProp,
  type LayoutChangeEvent,
} from 'react-native';

import { useNetworkStatus } from '../../hooks/queries/useOfflineRequestsQuery';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  FileIcon,
  PlusCircle,
  X,
} from '../icons';
import { AttachmentCountBadge } from './AttachmentCountBadge';
import { FieldFillCountBadge } from './FieldFillCountBadge';
import { SelectedFile } from './FileUpload';
import { colors, fontSize, fontWeight, lineHeight, spacing } from '../../theme';
import { PermissionAwareField } from '../PermissionAwareField';
import {
  FormFieldStackSpacingProvider,
  formFieldInStack,
} from './formFieldStackContext';
import { useResolvedDefaultSectionExpanded } from './stepFirstSubsectionExpandContext';
import { ATTACHMENT_COLORS } from '../../constants/attachmentColors';
import { SECTION_PERMISSION_FIELDS } from '../../constants/sectionIds';
import { useAuth } from '../../contexts/AuthContext';
import {
  useRequestStage,
  useRequestId,
} from '../../contexts/RequestStageContext';
import { useFieldPermissions } from '../../hooks/useFieldPermissions';
import { useRecentFilePickGuard } from '../../hooks/useRecentFilePickGuard';
import { useResponsive } from '../../hooks/useResponsive';
import {
  SavedAttachment,
  attachmentsForUploadStyleDisplay,
} from '../../services/attachmentService';
import { fieldPermissionService } from '../../services/auth/fieldPermissionService';
import { formatFileSize, formatDate } from '../../utils/attachmentFormatters';
import {
  validateFileForSecurity,
  buildAggregatedBlockedMessage,
  MAX_UPLOAD_SIZE_MB,
} from '../../utils/fileSecurityValidation';
import {
  hydrateOpportunitySubsectionExpanded,
  readOpportunitySubsectionExpanded,
  writeOpportunitySubsectionExpanded,
} from '../../utils/opportunitySubsectionExpandState';
import { resolveRepeatableInstanceAttachmentBaseSectionId } from '../../utils/repeatableInstanceAttachmentSections';
import { collectRichHtmlAttachmentIds } from '../../utils/richContent/richHtmlAttachmentRefs';
import { showToast } from '../../utils/toast';
import { AttachmentSectionList } from '../attachments';

export { formFieldInStack };

/**
 * Card shell for a Create Request / Request Detail subsection: optional title row, attachments,
 * and field children. Product name `SubsectionForm`; field-mapping layer still uses
 * `formSection` / `getFormSections` for matrix keys.
 *
 * **#1011:** Titled cards collapse by default (`defaultSectionExpanded` false). Title row + chevron
 * toggle field body and attachment dropdown on **all** steps using this component (or `StepForm`).
 */

// Runtime validation that required components exist
if (!AttachmentSectionList) {
  throw new Error(
    'AttachmentSectionList component not found. Ensure Phase 0-3 components are installed.',
  );
}

/**
 * Section description + populated-field summary under the card title. Off product-wide; step
 * copy and `titleSubtitleSummary` props stay wired. {@link FieldFillCountBadge} (`n/m`) is
 * unchanged and still updates from live form values passed as `fieldFillCount` / `fieldTotalCount`.
 */
const SUBSECTION_HEADER_SUBTITLE_VISIBLE = false;

type WithChildren = { children?: React.ReactNode };

/** Narrow a React element's props to its (optional) `children` field without `any`. */
const readChildren = (element: React.ReactElement): React.ReactNode =>
  (element.props as WithChildren).children;

/**
 * Check if a React element has any visible children recursively
 */
const hasVisibleChildren = (element: React.ReactElement): boolean => {
  const children = readChildren(element);

  if (!children) {
    return false;
  }

  const childArray = React.Children.toArray(children);

  for (const child of childArray) {
    if (typeof child === 'string' || typeof child === 'number') {
      if (typeof child === 'string' && child.trim() === '') {
        continue;
      }
      return true;
    }

    if (child === null || child === undefined || child === false) {
      continue;
    }

    if (React.isValidElement(child)) {
      if (child.type === React.Fragment && !hasVisibleChildren(child)) {
        continue;
      }

      const childProps = child.props as WithChildren;

      if (childProps.children !== undefined) {
        if (hasVisibleChildren(child)) {
          return true;
        }
      } else {
        // Elements without `children` (like FormInput) are considered visible.
        return true;
      }
    }
  }

  return false;
};

/**
 * Utility function to filter out null/undefined children
 * This ensures hidden fields don't create empty space in layouts
 */
const filterValidChildren = (children: React.ReactNode): React.ReactNode[] => {
  return React.Children.toArray(children).filter(child => {
    if (child === null || child === undefined || child === '') {
      return false;
    }

    if (React.isValidElement(child)) {
      if (child.type === React.Fragment && !hasVisibleChildren(child)) {
        return false;
      }

      const childProps = child.props as WithChildren;
      if (childProps.children !== undefined && !hasVisibleChildren(child)) {
        return false;
      }
    }

    return true;
  });
};

interface SubsectionFormProps {
  title?: string;
  /**
   * Descriptive line directly under the title row, still inside the header block so the
   * section’s bottom border (divider) sits below this text, not between title and subheader.
   * Not rendered while {@link SUBSECTION_HEADER_SUBTITLE_VISIBLE} is false; when re-enabled,
   * also hidden on small viewports ({@link useResponsive} `isSmallScreen`).
   */
  titleSubtitle?: string;
  /**
   * Merged with `sectionTitle` for the title `Text` (e.g. neutral text instead of brand orange per screen).
   */
  titleTextStyle?: any;
  children: React.ReactNode;
  containerStyle?: any;
  /**
   * If true, always show the section even if it appears empty
   * Useful for sections that should always be visible
   */
  alwaysShow?: boolean;
  /**
   * Optional content to render on the right side of the title row
   */
  rightContent?: React.ReactNode;
  /**
   * Unique section ID for attachment grouping (e.g., "facility-power.large-ups")
   */
  sectionId?: string;
  /**
   * Request ID for attachment and permission operations.
   * Falls back to RequestStageContext if not provided.
   */
  requestId?: string;
  /**
   * Request stage for permission checks (e.g., "DRAFT", "SITE_ASSESSMENT").
   * Falls back to RequestStageContext, then 'DRAFT' if not provided.
   */
  requestStage?: string;
  /**
   * Enable attachment functionality for this section
   */
  allowAttachments?: boolean;
  /**
   * Attachments associated with this section
   */
  attachments?: SavedAttachment[];
  /**
   * Rich HTML from this card's notes field. Images referenced there stay out of
   * the paperclip list and badge even if `attachmentSource` is missing.
   */
  inlineFieldHtml?: string;
  /**
   * Callback when paperclip icon is pressed to add attachments
   * @deprecated Use selectedFiles and onFilesSelected instead for inline file upload
   */
  onAttachFiles?: () => void;
  /**
   * Callback when an attachment is deleted
   */
  onDeleteAttachment?: (attachmentId: number) => void;
  /**
   * Callback when an attachment caption is changed
   */
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  /**
   * Pending caption edits from parent (survives step navigation/remounts)
   */
  pendingCaptionEdits?: Record<number, string>;
  /**
   * Selected files pending upload for this section
   */
  selectedFiles?: SelectedFile[];
  /**
   * Callback when files are selected for upload
   */
  onFilesSelected?: (files: SelectedFile[]) => void;
  /**
   * Whether the app is offline (disables file uploads)
   */
  isOffline?: boolean;
  /**
   * Whether the form is in edit mode
   */
  isEditing?: boolean;
  /**
   * Whether controls should be disabled
   */
  disabled?: boolean;
  /**
   * Override mobile detection (optional, auto-detected if not provided)
   */
  isMobile?: boolean;
  /**
   * Merged into the body area below the title (and optional `titleSubtitle`).
   */
  contentContainerStyle?: any;
  /**
   * Merged into the title-subtitle block when `titleSubtitle` is set.
   */
  titleSubtitleBlockStyle?: any;
  /**
   * Merged into the title / attachment action row in the card header.
   */
  headerContentStyle?: any;
  /**
   * Renders at the top of the card, above the title row (e.g. subsection tabs).
   */
  aboveTitle?: React.ReactNode;
  /**
   * When true (default when `title` is set), the title row / chevron toggles the field body and
   * attachment dropdown (#1011). Attachment paperclip remains a separate toggle when expanded.
   */
  collapsibleSection?: boolean;
  /**
   * Initial expanded state for the section body. When omitted, non-collapsible cards stay open;
   * the first collapsible card in the current step scope opens by default; others stay collapsed (#1011).
   */
  defaultSectionExpanded?: boolean;
  /**
   * When true, expands the section body (e.g. parent validation failed for a field in this card).
   * Re-applies when the prop toggles true so users see inline errors without hunting collapsed cards.
   */
  expandSection?: boolean;
  /**
   * When true with `sectionId`, expand/collapse survives step navigation and save remounts (OP-UX-02).
   */
  persistSectionExpanded?: boolean;
  /**
   * When set with `fieldTotalCount`, shows "n of m" beside the expand/collapse chevron (#1011).
   */
  fieldFillCount?: number;
  fieldTotalCount?: number;
  /**
   * One-line populated-field summary; when non-empty, replaces static `titleSubtitle` in the header.
   */
  titleSubtitleSummary?: string;
}

export const SubsectionForm: React.FC<SubsectionFormProps> = ({
  title,
  titleSubtitle,
  titleTextStyle,
  children,
  containerStyle,
  alwaysShow = false,
  rightContent,
  sectionId,
  requestId,
  requestStage,
  allowAttachments = false,
  attachments = [],
  inlineFieldHtml,
  onAttachFiles,
  onDeleteAttachment,
  onCaptionChange,
  pendingCaptionEdits: parentPendingCaptionEdits,
  selectedFiles = [],
  onFilesSelected,
  isOffline = false,
  isEditing = false,
  disabled = false,
  isMobile: isMobileProp,
  contentContainerStyle,
  titleSubtitleBlockStyle,
  headerContentStyle,
  aboveTitle,
  collapsibleSection,
  defaultSectionExpanded: defaultSectionExpandedProp,
  expandSection = false,
  persistSectionExpanded = false,
  fieldFillCount,
  fieldTotalCount,
  titleSubtitleSummary,
}) => {
  // Get user for permission checks
  const { user } = useAuth();

  // Use context as fallback for request stage and ID (avoids prop drilling)
  const contextStage = useRequestStage();
  const contextRequestId = useRequestId();
  const effectiveRequestStage = requestStage ?? contextStage ?? 'DRAFT';
  const effectiveRequestId = requestId ?? contextRequestId;

  // Check if permissions are initialized to determine when to hide empty sections
  const { isInitialized } = useFieldPermissions({
    enableCaching: true,
    requestStage: effectiveRequestStage,
    requestId: effectiveRequestId,
  });

  // Responsive layout (with prop override for testing/SSR)
  const { width: screenWidth } = useWindowDimensions();
  const calculatedMobile = screenWidth < 600;
  const isMobile = isMobileProp ?? calculatedMobile;
  const { isSmallScreen } = useResponsive();
  const guardRecentFilePicks = useRecentFilePickGuard();

  // Network status detection (with prop override)
  const isOnline = useNetworkStatus();
  const isActuallyOffline = isOffline || !isOnline;

  // Track rendered content to hide untitled empty shells (web: DOM children; native: layout height).
  const [hasContent, setHasContent] = useState(Platform.OS === 'web');
  /** Native: do not hide until first onLayout (avoids returning null before children mount). */
  const [nativeContentMeasured, setNativeContentMeasured] = useState(false);
  const contentRef = React.useRef<View | null>(null);

  // State for showing attachments area (toggled by paperclip)
  const [showAttachmentsArea, setShowAttachmentsArea] = useState(false);

  const hasTitleHeader = Boolean(title && String(title).trim());
  /**
   * #1011: collapse only on titled cards that are not `alwaysShow` (e.g. Supporting Documentation
   * attachment shell and upload dropzone must stay open).
   */
  const isCollapsibleSection =
    collapsibleSection !== undefined
      ? collapsibleSection
      : hasTitleHeader && !alwaysShow;
  const defaultSectionExpanded = useResolvedDefaultSectionExpanded(
    isCollapsibleSection,
    defaultSectionExpandedProp,
  );
  const shouldPersistExpand =
    persistSectionExpanded && Boolean(sectionId) && isCollapsibleSection;
  const resolveExpanded = useCallback(
    (fallbackDefault: boolean) => {
      if (!shouldPersistExpand) {
        return fallbackDefault;
      }
      return readOpportunitySubsectionExpanded(
        effectiveRequestId,
        sectionId as string,
        fallbackDefault,
      );
    },
    [shouldPersistExpand, effectiveRequestId, sectionId],
  );
  const [isSectionExpanded, setIsSectionExpanded] = useState(() =>
    resolveExpanded(defaultSectionExpanded),
  );

  useEffect(() => {
    if (!shouldPersistExpand) {
      return;
    }
    setIsSectionExpanded(resolveExpanded(defaultSectionExpanded));
  }, [
    shouldPersistExpand,
    effectiveRequestId,
    sectionId,
    defaultSectionExpanded,
    resolveExpanded,
  ]);

  useEffect(() => {
    if (!shouldPersistExpand || !sectionId) {
      return;
    }
    let active = true;
    void hydrateOpportunitySubsectionExpanded(
      effectiveRequestId,
      sectionId,
      defaultSectionExpanded,
    ).then(expanded => {
      if (active) {
        setIsSectionExpanded(expanded);
      }
    });
    return () => {
      active = false;
    };
  }, [
    shouldPersistExpand,
    effectiveRequestId,
    sectionId,
    defaultSectionExpanded,
  ]);

  useEffect(() => {
    if (expandSection) {
      setIsSectionExpanded(true);
      if (shouldPersistExpand && sectionId) {
        writeOpportunitySubsectionExpanded(effectiveRequestId, sectionId, true);
      }
    }
  }, [expandSection, shouldPersistExpand, sectionId, effectiveRequestId]);

  const toggleSectionExpanded = useCallback(() => {
    if (!isCollapsibleSection) {
      return;
    }
    setIsSectionExpanded(prev => {
      const next = !prev;
      if (!next) {
        setShowAttachmentsArea(false);
      }
      if (shouldPersistExpand && sectionId) {
        writeOpportunitySubsectionExpanded(effectiveRequestId, sectionId, next);
      }
      return next;
    });
  }, [
    isCollapsibleSection,
    shouldPersistExpand,
    sectionId,
    effectiveRequestId,
  ]);

  // State for section-level write permission (controls whether user can add/delete attachments)
  const [hasSectionWriteAccess, setHasSectionWriteAccess] = useState(false);

  // State for attachment operations
  const [pendingCaptionEdits, setPendingCaptionEdits] = useState<
    Record<number, string>
  >({});

  // Check section-level write permission when sectionId, user roles, or stage changes
  useEffect(() => {
    const checkSectionWriteAccess = async () => {
      // Fail closed until we confirm write access (API still enforces on upload).
      if (!sectionId || !user?.roles || user.roles.length === 0) {
        setHasSectionWriteAccess(false);
        return;
      }

      try {
        // Get the subsection-specific permission config
        const baseSectionId =
          resolveRepeatableInstanceAttachmentBaseSectionId(sectionId);
        const subsectionConfig = SECTION_PERMISSION_FIELDS[baseSectionId];

        if (!subsectionConfig) {
          if (__DEV__) {
            console.warn(`No permission config for sectionId: ${sectionId}`);
          }
          setHasSectionWriteAccess(false);
          return;
        }

        const { section: permissionSection, fields: subsectionFields } =
          subsectionConfig;

        // Get all accessible fields in the parent section
        const accessibleFieldsRaw =
          await fieldPermissionService.getAccessibleFields(
            user.roles,
            permissionSection,
            effectiveRequestStage,
            effectiveRequestId,
            user.email,
          );
        const accessibleFields = Array.isArray(accessibleFieldsRaw)
          ? accessibleFieldsRaw
          : [];

        // Filter to only the fields that belong to this specific subsection
        // and check if user has write access to any of them
        const writableSubsectionFields = accessibleFields.filter(
          ({ fieldName, access }) =>
            subsectionFields.includes(fieldName) && access.canWrite,
        );
        const hasWriteAccess = writableSubsectionFields.length > 0;

        setHasSectionWriteAccess(hasWriteAccess);
      } catch (error) {
        if (__DEV__) {
          console.error('Error checking section write access:', error);
        }
        setHasSectionWriteAccess(false);
      }
    };

    checkSectionWriteAccess();
  }, [
    sectionId,
    title,
    user?.roles,
    user?.email,
    effectiveRequestStage,
    effectiveRequestId,
  ]);

  const handleNativeContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (Platform.OS === 'web' || !isInitialized || alwaysShow) {
        return;
      }
      setNativeContentMeasured(true);
      setHasContent(event.nativeEvent.layout.height > 0);
    },
    [isInitialized, alwaysShow],
  );

  const displayAttachments = useMemo(() => {
    const referencedInlineIds = new Set(
      collectRichHtmlAttachmentIds(inlineFieldHtml ?? ''),
    );
    return attachmentsForUploadStyleDisplay(attachments, referencedInlineIds);
  }, [attachments, inlineFieldHtml]);

  const attachmentCount = displayAttachments.length;

  // Web: check DOM after permission-aware children resolve to null.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isInitialized || alwaysShow) {
      return;
    }

    const checkContent = () => {
      const element = contentRef.current as unknown as HTMLElement | null;
      if (!element) {
        return;
      }
      const hasVisibleContent =
        element.children != null && element.children.length > 0;
      setHasContent(hasVisibleContent);
    };

    checkContent();
    const timer1 = setTimeout(checkContent, 50);
    const timer2 = setTimeout(checkContent, 150);
    const timer3 = setTimeout(checkContent, 300);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isInitialized, children, alwaysShow]);

  const showSectionBody = !isCollapsibleSection || isSectionExpanded;
  /** Attachment controls stay visible when collapsed (#1011 UX). */
  const showAttachmentHeaderChrome = allowAttachments;
  const showFieldFillMetric =
    fieldTotalCount != null && fieldTotalCount > 0 && fieldFillCount != null;
  /**
   * Opportunity Project | Requestor tabs swap cards: Project Summary has attach chrome,
   * Requestor does not — reserve the same trailing width so the header bar height stays stable.
   */
  const reserveAttachmentHeaderChrome =
    !showAttachmentHeaderChrome && showFieldFillMetric;

  // Hide only untitled empty shells once permissions are resolved (subsection cards keep their header).
  const shouldHideUntitledEmptyShell =
    isInitialized &&
    !alwaysShow &&
    !hasTitleHeader &&
    !hasContent &&
    (Platform.OS === 'web' || nativeContentMeasured);
  if (shouldHideUntitledEmptyShell) {
    return null;
  }

  // Trigger file picker for web
  const triggerWebFilePicker = () => {
    if (Platform.OS !== 'web') return;

    const doc = (global as any).document;
    if (!doc) return;

    const input = doc.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.multiple = true;

    input.onchange = () => {
      const files = input.files;
      if (files && files.length > 0 && onFilesSelected) {
        const newFiles: SelectedFile[] = [];
        const blockedFiles: string[] = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          // Security validation (executable blocking + size check)
          const validation = validateFileForSecurity(
            { name: file.name, size: file.size, type: file.type },
            MAX_UPLOAD_SIZE_MB,
          );
          if (!validation.valid) {
            blockedFiles.push(file.name);
            continue;
          }

          const selectedFile: SelectedFile = {
            id: `${file.name}_${Date.now()}_${Math.random()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            file: file,
            createdAt: new Date().toISOString(),
          };
          newFiles.push(selectedFile);
        }

        // Show aggregated error toast if any files were blocked
        if (blockedFiles.length > 0) {
          showToast.error(buildAggregatedBlockedMessage(blockedFiles));
        }

        if (newFiles.length > 0) {
          onFilesSelected([...selectedFiles, ...newFiles]);
        }
      }
    };

    input.click();
  };

  // Handle choosing from document picker (files)
  const handleChooseFromFiles = async () => {
    try {
      const picker = await import('@react-native-documents/picker');
      const results = await picker.pick({
        type: [picker.types.allFiles, picker.types.audio],
        allowMultiSelection: true,
      });

      if (onFilesSelected) {
        const newFiles: SelectedFile[] = [];
        const blockedFiles: string[] = [];

        for (const file of results) {
          const fileName = file.name || 'Unknown file';

          // Security validation (executable blocking + size check)
          const validation = validateFileForSecurity(
            { name: fileName, size: file.size || 0, type: file.type || '' },
            MAX_UPLOAD_SIZE_MB,
          );
          if (!validation.valid) {
            blockedFiles.push(fileName);
            continue;
          }

          newFiles.push({
            id: `${fileName}_${Date.now()}_${Math.random()}`,
            name: fileName,
            size: file.size || 0,
            type: file.type || 'application/octet-stream',
            file: file,
            createdAt: new Date().toISOString(),
          });
        }

        // Show aggregated error toast if any files were blocked
        if (blockedFiles.length > 0) {
          showToast.error(buildAggregatedBlockedMessage(blockedFiles));
        }

        if (newFiles.length > 0) {
          const picks = guardRecentFilePicks(newFiles);
          if (picks.length > 0) {
            onFilesSelected([...selectedFiles, ...picks]);
          }
        }
      }
    } catch (err: unknown) {
      // User cancelled - this is expected behavior, not an error
      if (
        err &&
        typeof err === 'object' &&
        'code' in err &&
        err.code === 'DOCUMENT_PICKER_CANCELED'
      ) {
        return;
      }
      Alert.alert('Error', 'An error occurred while picking files.');
      console.error('DocumentPicker Error:', err);
    }
  };

  // Handle choosing from photo library
  const handleChooseFromPhotos = async () => {
    try {
      // Dynamic import to avoid TypeScript issues with platform-specific files
      const cameraServiceModule = await import(
        '../../services/cameraService.native'
      );
      const photos = await cameraServiceModule.cameraService.chooseFromLibrary(
        'mixed',
      );

      if (photos.length > 0 && onFilesSelected) {
        const blockedFiles: string[] = [];
        const validatedPhotos = photos.filter(photo => {
          // Security validation (user can access file system via "Browse" in photo picker)
          const validation = validateFileForSecurity(photo, MAX_UPLOAD_SIZE_MB);
          if (!validation.valid) {
            blockedFiles.push(photo.name);
            return false;
          }
          return true;
        });

        // Show aggregated error toast if any files were blocked
        if (blockedFiles.length > 0) {
          showToast.error(buildAggregatedBlockedMessage(blockedFiles));
        }

        const picks = guardRecentFilePicks(validatedPhotos);
        if (picks.length > 0) {
          onFilesSelected([...selectedFiles, ...picks]);
        }
      }
    } catch (error) {
      console.error('Error choosing from photos:', error);
    }
  };

  // Handle taking a photo
  const handleTakePhoto = async () => {
    try {
      const cameraServiceModule = await import(
        '../../services/cameraService.native'
      );
      const photo = await cameraServiceModule.cameraService.takePhoto();
      if (photo && onFilesSelected) {
        const picks = guardRecentFilePicks([photo]);
        if (picks.length > 0) {
          onFilesSelected([...selectedFiles, ...picks]);
        }
      }
    } catch (error) {
      console.error('Error taking photo:', error);
    }
  };

  // Handle recording video
  const handleRecordVideo = async () => {
    try {
      const cameraServiceModule = await import(
        '../../services/cameraService.native'
      );
      const video = await cameraServiceModule.cameraService.recordVideo();
      if (video && onFilesSelected) {
        const picks = guardRecentFilePicks([video]);
        if (picks.length > 0) {
          onFilesSelected([...selectedFiles, ...picks]);
        }
      }
    } catch (error) {
      console.error('Error recording video:', error);
    }
  };

  // Show action sheet for native media selection
  const showNativeMediaOptions = () => {
    if (Platform.OS === 'web') return;

    const options = [
      'Choose from Photos',
      'Choose from Files',
      'Take Photo',
      'Record Video',
      'Cancel',
    ];

    const handlers = [
      handleChooseFromPhotos,
      handleChooseFromFiles,
      handleTakePhoto,
      handleRecordVideo,
    ];

    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: options.length - 1,
          title: 'Add Attachment',
          message: 'Choose how you want to add files',
        },
        buttonIndex => {
          if (buttonIndex < handlers.length) {
            handlers[buttonIndex]();
          }
        },
      );
    } else {
      // Android uses Alert with buttons
      Alert.alert('Add Attachment', 'Choose how you want to add files', [
        { text: 'Choose from Photos', onPress: handleChooseFromPhotos },
        { text: 'Choose from Files', onPress: handleChooseFromFiles },
        { text: 'Take Photo', onPress: handleTakePhoto },
        { text: 'Record Video', onPress: handleRecordVideo },
        { text: 'Cancel', style: 'cancel' },
      ]);
    }
  };

  // Trigger the appropriate file picker based on platform
  const triggerFilePicker = () => {
    if (
      disabled ||
      isActuallyOffline ||
      !onFilesSelected ||
      !hasSectionWriteAccess
    )
      return;

    if (Platform.OS === 'web') {
      triggerWebFilePicker();
    } else {
      showNativeMediaOptions();
    }
  };

  // Toggle attachments dropdown visibility (file icon button)
  const handleToggleAttachments = (e?: any) => {
    // Stop propagation to prevent collapsible toggle
    if (e?.stopPropagation) {
      e.stopPropagation();
    }

    // Toggle the attachments area visibility
    setShowAttachmentsArea(prev => !prev);

    // Legacy callback behavior
    if (
      onAttachFiles &&
      !disabled &&
      !isActuallyOffline &&
      !showAttachmentsArea
    ) {
      onAttachFiles();
    }
  };

  // Handle plus button press to add files (only in edit mode)
  const handleAddFilesPress = (e?: any) => {
    // Stop propagation
    if (e?.stopPropagation) {
      e.stopPropagation();
    }

    // Open file picker
    if (onFilesSelected && !disabled && !isActuallyOffline) {
      triggerFilePicker();
    }

    // Also open the attachments area if not already open
    if (!showAttachmentsArea) {
      setShowAttachmentsArea(true);
    }
  };

  // Handle caption change for saved attachments
  const handleCaptionChangeLocal = (attachmentId: number, caption: string) => {
    // Store locally for UI responsiveness
    setPendingCaptionEdits(prev => ({
      ...prev,
      [attachmentId]: caption,
    }));

    // Notify parent component to persist the change
    if (onCaptionChange) {
      onCaptionChange(attachmentId, caption);
    }
  };

  // Handle attachment deletion - compatible with AttachmentSectionList interface
  const handleDeleteAttachmentById = async (attachmentId: number) => {
    try {
      // AttachmentListItem component handles deletion confirmation and UI
      // But we need to ensure parent is notified to refresh the attachment list
      // Note: AttachmentListItem calls attachmentService.deleteAttachment() internally
      // This callback is primarily for parent refresh notification
      if (onDeleteAttachment) {
        await Promise.resolve(onDeleteAttachment(attachmentId));
      }
    } catch (error) {
      console.error('Error in delete attachment callback:', error);
      // Error is already handled by AttachmentListItem, just log here
    }
  };

  // Remove a staged (pending) file before it's saved
  const handleRemoveStagedFile = (fileId: string) => {
    if (onFilesSelected) {
      const updatedFiles = selectedFiles.filter(f => f.id !== fileId);
      onFilesSelected(updatedFiles);
    }
  };

  // Update caption for a staged file
  const handleUpdateStagedCaption = (fileId: string, caption: string) => {
    if (onFilesSelected) {
      const updatedFiles = selectedFiles.map(f =>
        f.id === fileId ? { ...f, caption } : f,
      );
      onFilesSelected(updatedFiles);
    }
  };

  // Calculate total count (saved + staged)
  const stagedCount = selectedFiles.length;
  const totalCount = attachmentCount + stagedCount;

  // Render the header with title, file icon for attachments toggle, and plus button for adding files
  const renderHeader = () => {
    if (!title) return null;

    const summaryLine =
      titleSubtitleSummary != null &&
      String(titleSubtitleSummary).trim().length > 0
        ? String(titleSubtitleSummary).trim()
        : '';
    const staticSubtitle =
      titleSubtitle != null ? String(titleSubtitle).trim() : '';
    const resolvedSubtitle =
      summaryLine.length > 0 ? summaryLine : staticSubtitle;
    const hasTitleSubtitle = resolvedSubtitle.length > 0;
    const showTitleSubtitle =
      SUBSECTION_HEADER_SUBTITLE_VISIBLE && hasTitleSubtitle && !isSmallScreen;

    const sectionExpandChevron = isCollapsibleSection ? (
      <View style={styles.sectionChevronWrap} accessibilityElementsHidden>
        {isSectionExpanded ? (
          <ChevronDown
            size={Platform.OS === 'web' ? 16 : 18}
            color={colors.textSecondary}
          />
        ) : (
          <ChevronRight
            size={Platform.OS === 'web' ? 16 : 18}
            color={colors.textSecondary}
          />
        )}
      </View>
    ) : null;

    const subtitleBlock = showTitleSubtitle ? (
      <View style={[styles.titleSubtitleBlock, titleSubtitleBlockStyle]}>
        <Text
          style={styles.titleSubtitleText}
          accessibilityRole="text"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {resolvedSubtitle}
        </Text>
      </View>
    ) : null;

    const fieldFillMetric = showFieldFillMetric ? (
      <FieldFillCountBadge filled={fieldFillCount} total={fieldTotalCount} />
    ) : null;

    const headerTrailing = (
      <View style={styles.headerTrailingRailCluster} pointerEvents="box-none">
        {rightContent != null ? (
          <View style={styles.headerTrailingOptional} pointerEvents="box-none">
            {rightContent}
          </View>
        ) : null}

        {!reserveAttachmentHeaderChrome ? fieldFillMetric : null}

        {showAttachmentHeaderChrome ? (
          <View
            style={styles.attachmentControlCluster}
            pointerEvents="box-none"
          >
            {isEditing &&
            onFilesSelected &&
            !disabled &&
            !isActuallyOffline &&
            hasSectionWriteAccess ? (
              <TouchableOpacity
                style={styles.addFileButtonInline}
                onPress={handleAddFilesPress}
                accessibilityLabel={`Add files to ${title}`}
                accessibilityHint="Opens file picker to add attachments"
              >
                <PlusCircle
                  size={Platform.OS === 'web' ? 16 : 18}
                  color={colors.success}
                />
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.attachmentButton}
              onPress={handleToggleAttachments}
              accessibilityLabel={`${
                showAttachmentsArea ? 'Hide' : 'Show'
              } attachments for ${title}`}
              accessibilityHint="Toggles the attachments area"
            >
              <View style={styles.attachmentIconContainer}>
                <FileIcon
                  size={Platform.OS === 'web' ? 18 : 20}
                  color={
                    showAttachmentsArea ? colors.primary : colors.textSecondary
                  }
                />
                <View
                  style={styles.attachmentBadgeOverlay}
                  pointerEvents="none"
                >
                  <AttachmentCountBadge count={totalCount} size="small" />
                </View>
              </View>
            </TouchableOpacity>
          </View>
        ) : reserveAttachmentHeaderChrome ? (
          <View
            testID="attachment-header-chrome-reserve"
            style={[
              styles.attachmentControlCluster,
              styles.attachmentHeaderChromeReserve,
              styles.attachmentHeaderChromeReserveWithMetric,
            ]}
            pointerEvents="box-none"
          >
            {fieldFillMetric}
          </View>
        ) : (
          fieldFillMetric
        )}

        {sectionExpandChevron}
      </View>
    );

    const headerTitleBlock = (
      <View style={styles.headerTitleCluster}>
        <View style={styles.titleContainer}>
          {/* #1011: subsection titles never show icons in the request flow */}
          <Text
            style={[styles.sectionTitle, titleTextStyle]}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {title}
          </Text>
        </View>
      </View>
    );

    const headerBarBody = (
      <View
        style={[
          showTitleSubtitle ? styles.headerRowWithSubtitle : styles.headerRow,
          headerContentStyle,
        ]}
      >
        {showTitleSubtitle ? (
          <View style={styles.headerLeadingWithSubtitle}>
            {headerTitleBlock}
            {subtitleBlock}
          </View>
        ) : (
          headerTitleBlock
        )}
        <View style={styles.headerTrailingRail} pointerEvents="box-none">
          {headerTrailing}
        </View>
      </View>
    );

    const expandCollapseLabel = `${
      isSectionExpanded ? 'Collapse' : 'Expand'
    } ${title}`;

    return (
      <View style={styles.headerTouchable}>
        {isCollapsibleSection ? (
          <TouchableOpacity
            onPress={toggleSectionExpanded}
            style={[
              styles.headerPressable,
              Platform.OS === 'web' && styles.headerPressableWeb,
            ]}
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel={expandCollapseLabel}
            accessibilityState={{ expanded: isSectionExpanded }}
          >
            {headerBarBody}
          </TouchableOpacity>
        ) : (
          headerBarBody
        )}
      </View>
    );
  };

  // Render compact attachment list when attachments area is visible
  // This appears under the title, before form fields
  const renderAttachmentList = () => {
    if (!allowAttachments || !showAttachmentsArea) {
      return null;
    }

    const hasAttachments = attachmentCount > 0;
    const hasStagedFiles = stagedCount > 0;

    return (
      <View style={styles.attachmentDropdown}>
        {/* Staged files (pending upload) */}
        {hasStagedFiles && (
          <View style={styles.compactAttachmentList}>
            {selectedFiles.map(file => (
              <View key={file.id} style={styles.stagedFileRow}>
                <View style={styles.compactAttachmentItem}>
                  {/* File icon */}
                  <FileText
                    size={12}
                    color={ATTACHMENT_COLORS.FILE_ICON_COLOR}
                  />

                  {/* File name and metadata */}
                  <View style={styles.fileInfoContainer}>
                    <Text style={styles.compactFileName} numberOfLines={1}>
                      {file.name}
                    </Text>
                    {/* Date and size inline below filename on mobile */}
                    {Platform.OS !== 'web' && (
                      <Text style={styles.mobileMetaText}>
                        {formatDate(file.createdAt)} ·{' '}
                        {formatFileSize(file.size)}
                      </Text>
                    )}
                  </View>

                  {/* Date and size columns on web only */}
                  {Platform.OS === 'web' && (
                    <>
                      <Text style={styles.compactDateColumn}>
                        {formatDate(file.createdAt)}
                      </Text>
                      <Text style={styles.compactSizeColumn}>
                        {formatFileSize(file.size)}
                      </Text>
                    </>
                  )}

                  {/* Remove button */}
                  <TouchableOpacity
                    style={styles.compactActionBtn}
                    onPress={() => handleRemoveStagedFile(file.id)}
                    accessibilityLabel={`Remove ${file.name}`}
                  >
                    <X
                      size={14}
                      color={ATTACHMENT_COLORS.DELETE_BUTTON_COLOR}
                    />
                  </TouchableOpacity>
                </View>

                {/* Caption input */}
                <TextInput
                  style={styles.captionInput}
                  value={file.caption || ''}
                  onChangeText={text =>
                    handleUpdateStagedCaption(file.id, text)
                  }
                  placeholder="Add caption (optional)"
                  placeholderTextColor={ATTACHMENT_COLORS.METADATA_COLOR}
                  multiline={false}
                />
              </View>
            ))}
          </View>
        )}

        {/* Saved attachments - use AttachmentSectionList component */}
        {hasAttachments && (
          <AttachmentSectionList
            attachments={displayAttachments}
            isEditing={isEditing && hasSectionWriteAccess}
            isMobile={isMobile}
            onCaptionChange={handleCaptionChangeLocal}
            onDelete={handleDeleteAttachmentById}
            pendingCaptionEdits={{
              ...parentPendingCaptionEdits,
              ...pendingCaptionEdits,
            }}
            showHeaders={!isMobile}
            offlineMode={isActuallyOffline}
          />
        )}

        {/* No attachments message - compact */}
        {!hasAttachments && !hasStagedFiles && (
          <Text style={styles.compactNoAttachments}>No attachments</Text>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.section, containerStyle]}>
      {aboveTitle != null ? (
        <View style={styles.aboveTitleBlock}>{aboveTitle}</View>
      ) : null}
      {renderHeader()}
      {/*
        #1011: Attachment dropdown is visible regardless of body collapse state — users can open the
        file list from a collapsed card via the header paperclip. The helper itself returns null
        when `showAttachmentsArea` is false, so this is cheap when no one toggled it open.
      */}
      {renderAttachmentList()}
      {showSectionBody ? (
        <View
          style={
            title
              ? [styles.sectionContent, contentContainerStyle]
              : [styles.sectionContentNoTitle, contentContainerStyle]
          }
        >
          <View
            ref={contentRef}
            collapsable
            onLayout={handleNativeContentLayout}
            style={styles.contentMeasure}
          >
            {children}
          </View>
        </View>
      ) : null}
    </View>
  );
};

SubsectionForm.displayName = 'SubsectionForm';

/**
 * When enabled, web `FormRow` / `FormColumn` use a single stacked column (same as narrow web /
 * native), regardless of viewport — used for Phase 1 create-request steps (#918).
 */
const FormStackColumnsOnWebContext = React.createContext(false);

export function FormStackColumnsOnWebProvider({
  children,
  enabled,
}: {
  children: React.ReactNode;
  /** When true, web form rows stack in one column like mobile. No effect on native. */
  enabled: boolean;
}): React.ReactElement {
  return (
    <FormStackColumnsOnWebContext.Provider value={enabled}>
      {children}
    </FormStackColumnsOnWebContext.Provider>
  );
}

interface FormRowProps {
  children: React.ReactNode;
  containerStyle?: any;
}

export const FormRow: React.FC<FormRowProps> = ({
  children,
  containerStyle,
}) => {
  const isWeb = Platform.OS === 'web';
  const { isSmallScreen } = useResponsive();
  const stackColumnsOnWeb = React.useContext(FormStackColumnsOnWebContext);

  // Filter out hidden/null children to prevent empty space
  // Use useMemo to re-evaluate when children change (important for async permission loading)
  const validChildren = React.useMemo(
    () => filterValidChildren(children),
    [children],
  );

  /**
   * {@link PermissionAwareField} often renders `null` after permission resolution, but
   * `filterValidChildren` still sees nested inputs — leaving an empty {@link FormRow}/{@link FormColumn}
   * shell (extra divs / spacing around e.g. Manufacturer vs Model on Rack Power).
   */
  const passthroughPermissionRowChild = React.useMemo(() => {
    if (validChildren.length !== 1) {
      return undefined;
    }
    const only = validChildren[0];
    if (!React.isValidElement(only)) {
      return undefined;
    }
    if (only.type === PermissionAwareField) {
      return only;
    }
    if (only.type === FormColumn) {
      const inner = filterValidChildren(
        (only.props as { children?: React.ReactNode }).children,
      );
      if (
        inner.length === 1 &&
        React.isValidElement(inner[0]) &&
        inner[0].type === PermissionAwareField
      ) {
        return inner[0];
      }
    }
    return undefined;
  }, [validChildren]);

  // Web: wide = side-by-side columns; narrow = stack. Native: always stack with `formFieldGroupGap`
  // between columns (same rhythm as `FormFieldStack` / `StepForm`).
  const dynamicStyle = React.useMemo(() => {
    if (!isWeb) {
      return {
        flexDirection: 'column' as const,
        gap: validChildren.length > 1 ? spacing.formFieldGroupGap : 0,
        alignItems: 'stretch' as const,
      };
    }
    const isDesktopWeb = !isSmallScreen && !stackColumnsOnWeb;
    const isMultiCol = isDesktopWeb && validChildren.length > 1;
    // Any non–desktop-web row is a **column** stack (narrow web, native via separate branch, or
    // `FormStackColumnsOnWebProvider`). Multi-child stacks need `formFieldGroupGap` between field
    // groups; wide + `stackColumnsOnWeb` used to set `gap: 0` here, which removed space between
    // e.g. “Dropped ceiling” and “Plenum” on Site dimensions (#918).
    const isStackedMultiCol = !isDesktopWeb && validChildren.length > 1;
    // Wide web, two+ columns: `stretch` cross-axis so each `FormColumn` gets the same row height.
    // `flex-end` used to bottom-align columns and caused overlapping labels/controls on RN-web
    // with `FormFieldLabelWithInfo` + `NumberInput` in one column.
    return {
      flexDirection: isDesktopWeb ? ('row' as const) : ('column' as const),
      gap: isMultiCol
        ? spacing.fieldColumnGapWeb
        : isStackedMultiCol
        ? spacing.formFieldGroupGap
        : 0,
      alignItems: 'stretch' as const,
    };
  }, [isWeb, isSmallScreen, stackColumnsOnWeb, validChildren.length]);

  if (validChildren.length === 0) {
    return null;
  }

  if (passthroughPermissionRowChild !== undefined) {
    return <>{passthroughPermissionRowChild}</>;
  }

  return (
    <View style={[styles.formRow, dynamicStyle, containerStyle]}>
      {validChildren}
    </View>
  );
};

interface FormColumnProps {
  children: React.ReactNode;
  flex?: number;
  containerStyle?: any;
}

export const FormColumn: React.FC<FormColumnProps> = ({
  children,
  flex = 1,
  containerStyle,
}) => {
  const isWeb = Platform.OS === 'web';
  const { isSmallScreen } = useResponsive();
  const stackColumnsOnWeb = React.useContext(FormStackColumnsOnWebContext);
  // Filter out hidden/null children to prevent empty space
  // Use useMemo to re-evaluate when children change (important for async permission loading)
  const validChildren = React.useMemo(
    () => filterValidChildren(children),
    [children],
  );

  // If no valid children, don't render the column at all
  if (validChildren.length === 0) {
    return null;
  }

  // Do **not** use a default `flex: 1` on the column: when `FormRow` is a column (native or narrow
  // web), `flex: 1` on each child makes them **split the row’s height** and fields overlap. Only
  // apply `flex: 1` for wide web side‑by‑side (paired) columns.
  const usePairedFlex = isWeb && !isSmallScreen && !stackColumnsOnWeb;
  const columnFlexStyle = usePairedFlex
    ? { flex, minWidth: 0 as const }
    : ({
        flexGrow: 0,
        flexShrink: 0,
        alignSelf: 'stretch' as const,
        minWidth: 0,
        width: '100%' as const,
        flexBasis: 'auto' as const,
      } as const);

  return <View style={[columnFlexStyle, containerStyle]}>{validChildren}</View>;
};

// Add displayName for debugging and component detection
FormColumn.displayName = 'FormColumn';

interface FormFieldStackProps {
  children: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
}

/**
 * One vertical column of form groups with a single 16px rhythm (`spacing.formFieldGroupGap`).
 * Use for section card bodies (with `SubsectionForm`) instead of relying on each control’s
 * `formGroup` `marginBottom`, which can stack with `fieldRow` / wrappers on web.
 * Pair with `formFieldInStack` on `FormInput`, `FormDateField`, `FormYesNoChoice`, etc.
 *
 * When every row/field is permission-hidden, returns `null` so `SubsectionForm`’s web empty-body
 * detection can hide the card instead of leaving a zero-height bordered shell.
 */
export const FormFieldStack: React.FC<FormFieldStackProps> = ({
  children,
  containerStyle,
}) => {
  const validChildren = React.useMemo(
    () => filterValidChildren(children),
    [children],
  );
  if (validChildren.length === 0) {
    return null;
  }
  return (
    <FormFieldStackSpacingProvider>
      <View style={[styles.formFieldStack, containerStyle]}>
        {validChildren}
      </View>
    </FormFieldStackSpacingProvider>
  );
};
FormFieldStack.displayName = 'FormFieldStack';

const styles = StyleSheet.create({
  aboveTitleBlock: {
    ...Platform.select({
      web: {
        paddingTop: spacing.formSectionContentPaddingWeb,
        paddingHorizontal: spacing.formSectionContentPaddingWeb,
        paddingBottom: spacing.sm,
      },
      default: {
        paddingTop: spacing.md,
        paddingHorizontal: spacing.md,
        paddingBottom: spacing.sm,
      },
    }),
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.borderHairline,
  },
  formFieldStack: {
    width: '100%' as const,
    flexDirection: 'column' as const,
    gap: spacing.formFieldGroupGap,
  },
  section: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.cardBorderRadius,
    marginBottom: spacing.pdStepSubsectionGap,
    borderWidth: 1,
    borderColor: colors.borderCard,
    overflow: 'visible',
  },
  headerTouchable: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderHairline,
    overflow: 'visible',
  },
  /** Entire title + subtitle bar is one expand/collapse target when `collapsibleSection`. */
  headerPressable: {
    width: '100%',
  },
  headerPressableWeb: {
    cursor: 'pointer',
    alignSelf: 'stretch',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding:
      Platform.OS === 'web' ? spacing.formSectionContentPaddingWeb : spacing.md,
    gap: 0,
    overflow: 'visible',
  },
  /** Title + icon; tap expands/collapses when `collapsibleSection` (#1011). */
  headerTitleCluster: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: spacing.sm,
    gap: spacing.xs,
  },
  /** Single section expand/collapse affordance — trailing edge only. */
  sectionChevronWrap: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: spacing.xs,
  },
  /** `rightContent` + attachment control cluster (Sally: tools on the right). */
  headerTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  /**
   * Title + subtitle in one bar — single padding pass (do not stack with `headerRow` padding).
   * Trailing rail uses stretch + justifyContent center for chevron vertical alignment.
   */
  headerRowWithSubtitle: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    overflow: 'visible',
    paddingVertical: spacing.md,
    paddingHorizontal:
      Platform.OS === 'web' ? spacing.formSectionContentPaddingWeb : spacing.md,
  },
  headerLeadingWithSubtitle: {
    flex: 1,
    minWidth: 0,
    marginRight: spacing.sm,
  },
  headerTrailingRail: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    flexShrink: 0,
  },
  titleContainer: {
    flexShrink: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  /** Default: Vertiv orange card/subsection titles. Neutral overrides: `titleTextStyle` (e.g. Site Walk / Site information). */
  sectionTitle: {
    fontSize:
      Platform.OS === 'web'
        ? fontSize.sectionTitle
        : fontSize.formSectionTitleCompact,
    lineHeight:
      Platform.OS === 'web'
        ? lineHeight.sectionTitle
        : lineHeight.formSectionTitleCompact,
    fontWeight: fontWeight.semibold,
    color: colors.primary,
  },
  titleSubtitleBlock: {
    backgroundColor: colors.backgroundPrimary,
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    maxWidth: '50%',
    alignSelf: 'flex-start',
  },
  titleSubtitleText: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.body,
    color: colors.textSecondary,
  },
  headerTrailingRailCluster: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  headerTrailingOptional: {
    flexShrink: 0,
  },
  /** + and paperclip grouped tight (matches pre-#1011 split layout on secure). */
  attachmentControlCluster: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    overflow: 'visible',
  },
  /**
   * Matches `addFileButtonInline` / `attachmentButton` row height (paddingVertical 6×2 + icon).
   * Requestor tab reserve must not exceed this or the header bar grows vs Project Summary.
   */
  attachmentHeaderChromeReserve: Platform.select({
    web: {
      minWidth: 56,
      minHeight: 30,
    },
    default: {
      minWidth: 60,
      minHeight: 32,
    },
  }),
  /** Fill badge sits in the attachment slot (Requestor tab — no gap before chevron). */
  attachmentHeaderChromeReserveWithMetric: {
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  attachmentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingRight: 6,
    paddingLeft: 0,
    overflow: 'visible',
    ...Platform.select({
      web: { cursor: 'pointer' as const },
      default: {},
    }),
  },
  attachmentIconContainer: {
    position: 'relative',
    width: Platform.OS === 'web' ? 18 : 20,
    height: Platform.OS === 'web' ? 18 : 20,
    overflow: 'visible',
  },
  attachmentBadgeOverlay: {
    position: 'absolute',
    top: -8,
    right: -10,
    zIndex: 1,
  },
  addFileButton: {
    padding: 6,
    marginLeft: 4,
  },
  addFileButtonInline: {
    paddingVertical: 6,
    paddingLeft: 6,
    paddingRight: 0,
    ...Platform.select({
      web: { cursor: 'pointer' as const },
      default: {},
    }),
  },
  chevronButton: {
    padding: 4,
  },
  contentMeasure: {
    width: '100%',
  },
  sectionContent: {
    // Web: longhand so `contentContainerStyle` (e.g. `StepForm` top tight) can override
    // `paddingTop` without fighting the `padding` shorthand.
    ...Platform.select({
      web: {
        paddingTop: spacing.formSectionContentPaddingWeb,
        paddingBottom: spacing.formSectionContentPaddingWeb,
        paddingHorizontal: spacing.formSectionContentPaddingWeb,
        overflow: 'visible' as const,
      },
      default: { padding: spacing.sm, overflow: 'visible' as const },
    }),
  },
  sectionContentNoTitle: {
    ...Platform.select({
      web: {
        paddingTop: spacing.formSectionContentPaddingWeb,
        paddingBottom: spacing.formSectionContentPaddingWeb,
        paddingHorizontal: spacing.formSectionContentPaddingWeb,
        overflow: 'visible' as const,
      },
      default: { padding: spacing.sm, overflow: 'visible' as const },
    }),
  },
  deletingItem: {
    opacity: 0.5,
  },
  // Compact attachment dropdown styles (appears under title)
  attachmentDropdown: {
    paddingHorizontal:
      Platform.OS === 'web' ? spacing.formSectionContentPaddingWeb : spacing.sm,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderHairline,
    backgroundColor: colors.surfaceFormBandMuted,
  },
  compactAttachmentList: {
    gap: 4,
  },
  attachmentItemWrapper: {
    gap: 2,
  },
  mobileMetaRow: {
    paddingLeft: 26,
    paddingBottom: 2,
  },
  mobileMetaText: {
    fontSize: fontSize.formAttachmentMobileMeta,
    lineHeight: lineHeight.formAttachmentMobileMeta,
    color: colors.textMuted,
  },
  compactAttachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.formControlBorderRadius,
    gap: 6,
  },
  fileInfoContainer: {
    flex: 1,
    gap: 2,
    minWidth: Platform.OS === 'web' ? undefined : 100, // Ensure minimum width on mobile
  },
  compactFileName: {
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.body,
    color: colors.textSecondary,
    ...Platform.select({
      web: {},
      default: {
        flexShrink: 1, // Allow text to shrink on mobile
      },
    }),
  },
  compactDateColumn: {
    width: 75,
    fontSize: fontSize.formAttachmentMeta,
    lineHeight: lineHeight.formAttachmentMeta,
    color: colors.textSecondary,
    textAlign: 'right',
  },
  compactSizeColumn: {
    width: 55,
    fontSize: fontSize.formAttachmentMeta,
    lineHeight: lineHeight.formAttachmentMeta,
    color: colors.textSecondary,
    textAlign: 'right',
  },
  compactActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    minWidth: Platform.OS === 'web' ? 72 : 48, // Smaller minWidth on mobile to give more space to filename
    flexShrink: 0, // Prevent actions from shrinking
  },
  compactActionBtn: {
    padding: 4,
  },
  compactNoAttachments: {
    fontSize: 11,
    color: ATTACHMENT_COLORS.METADATA_COLOR,
    fontStyle: 'italic',
    paddingVertical: 4,
  },
  // Staged file styles
  stagedFileRow: {
    gap: 4,
  },
  captionInput: {
    backgroundColor: ATTACHMENT_COLORS.CARD_BACKGROUND_COLOR,
    borderWidth: 1,
    borderColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontSize: 11,
    color: ATTACHMENT_COLORS.FILENAME_COLOR,
    marginLeft: 18,
  },
  pendingFilesContainer: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.surfaceWarningMuted,
    borderRadius: spacing.formControlBorderRadius,
  },
  pendingFilesText: {
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.body,
    color: colors.textOnWarningMuted,
  },
  // FormRow and FormColumn styles
  formRow: {
    marginBottom: 0,
    overflow: 'visible',
  },
  formRowMobile: {
    // Stack fields vertically on mobile (matches native behavior)
    flexDirection: 'column',
    gap: 0,
  },
  formRowDesktop: {
    // Display fields horizontally on desktop
    flexDirection: 'row',
    gap: spacing.fieldColumnGapWeb,
  },
});
